import { TextLayer, IconLayer } from '@deck.gl/layers';
import { CompositeLayer } from '@deck.gl/core';
import { PickInfo } from 'deck.gl';
import { IColor } from '../../../../types/IColor';
import { LassoMode, LayerIds } from '../../constants';
import {
  RouteDtoModelType, RouteTimelineDtoModelType, MapMarkerType, VisitLocationDtoModelType,
} from '../../../../store/mst/models';
import ICON_MAPPING, { ICON_LAYER } from '../iconMapping';
import {
  LayerType, getContentSize, getMarkerSize, getPixelOffset, getMarkerLength, getMarkerText,
} from './markerHelpers';
import { VisitStatus } from '../../../../types/TVisit';
import { MAP_DEFAULT_ZOOM } from '../../../../store/mst/MapView';
import { MARKER_ICON_TRANSITION_DURATION } from '../../../../constants';
import { reverseLinearScale } from './helpers/zIndexHelpers';
import { buildClusterIndex, clusterDataAtSameLocation } from './helpers/clusterDataHelpers';


const BASE_COLOR = [255, 255, 255];
// For further reference, MISSED_COLOR = [207, 35, 35];

const TEXT_SECONDARY_COLOR = [40, 43, 49];
const PRIMARY_6 = [23, 80, 227];

const BASE_TRANSITION = {
  getPixelOffset: {
    duration: MARKER_ICON_TRANSITION_DURATION,
    enter: () => 0,
  },
};
interface MarkerIconLayerProps {
  id: string;
  visible: boolean;
  onHover: (hovered: PickInfo<MapMarkerType>) => void;
  onClick?: (hovered: PickInfo<MapMarkerType>, event?: any) => void;
  onDragStart?: (picked: PickInfo<MapMarkerType>) => void;
  onDrag?: (picked: PickInfo<MapMarkerType>) => void;
  onDragEnd?: (picked: PickInfo<MapMarkerType>) => void;
  selectedRoute?: RouteDtoModelType;
  hoveredRoute?: RouteDtoModelType;
  data: MapMarkerType[];
  hoveredMarker?: RouteTimelineDtoModelType | VisitLocationDtoModelType;
  selectedMarker?: RouteTimelineDtoModelType | VisitLocationDtoModelType;
  hoveredMarkers?: RouteTimelineDtoModelType[] | VisitLocationDtoModelType[];
  selectedMarkers?: RouteTimelineDtoModelType[] | VisitLocationDtoModelType[];
  zoom?: number;
  enableClustersAtSameLocation: boolean;
  lassoMode?: LassoMode | null;
}

class MarkerIconLayer extends CompositeLayer<MapMarkerType, MarkerIconLayerProps> {
  static shouldUpdateState({ changeFlags }: any) {
    return changeFlags.somethingChanged;
  }

  updateState({ props, oldProps, changeFlags }: any) {
    const rebuildIndex = changeFlags.dataChanged || props.sizeScale !== oldProps.sizeScale;
    
    if (rebuildIndex) {
      // Cluster nearby points to allow clean zIndex ranking
      const index = buildClusterIndex(props.data);
      this.setState({ index });
    }

    const z = Math.floor(this.props.zoom ?? MAP_DEFAULT_ZOOM);
    if (rebuildIndex || z !== this.state.z) {

      // Cluster again but only on points at the exact same location
      const clusteredData = clusterDataAtSameLocation(props.enableClustersAtSameLocation, this.state.index, z);

      if (rebuildIndex) {
        this.setState({
          data: clusteredData,
          totalCount: clusteredData.length,
          z,
        });
      }
    }
  }

  renderLayers() {
    const {
      id, onHover, onClick, onDragStart, onDrag, onDragEnd, visible, hoveredMarker, selectedMarker, selectedMarkers, zoom, selectedRoute, hoveredRoute, hoveredMarkers, lassoMode,
    } = this.props;

    const {
      data,
    } = this.state;

    const selectedMarkerIds = new Set(selectedMarkers?.map((marker) => marker.id));
    const isSelected = (v: MapMarkerType): boolean => {
      if (v.properties.cluster) {
        return !!(selectedMarker && v.properties.itemsIds?.includes(selectedMarker?.id))
          || (v.properties.itemsIds?.some((itemId: string) => selectedMarkerIds.has(itemId)) ?? false);
      }
      return selectedMarker?.id === v.properties.id || selectedMarkerIds.has(v.properties.id);
    };
    const isHovered = (v: MapMarkerType): boolean => {
      if (hoveredMarkers) {
        return v.properties.itemsIds?.includes(hoveredMarkers?.[0].id) || (hoveredMarkers?.[0].id === v.properties.id);
      }
      if (v.properties.cluster) {
        return !!(hoveredMarker && !!v.properties.itemsIds.includes(hoveredMarker?.id));
      }

      return hoveredMarker?.id === v.properties?.id;
    };
    const isActive = (v: MapMarkerType): boolean => isHovered(v) || isSelected(v);
    const isSelectedRoute = (v: MapMarkerType) => selectedRoute?.id === v.properties.routeId;
    const isHoveredRoute = (v: MapMarkerType) => hoveredRoute?.id === v.properties.routeId;
    const isActiveMarkerColor = (v: MapMarkerType) => (isSelectedRoute(v) || isHoveredRoute(v) || (!selectedRoute && !hoveredRoute && !lassoMode)) && !v.properties.isPending;
    const isSelectedSuggestion = (v: MapMarkerType) => id === LayerIds.STOP_SCATTERPLOT_SUGGESTIONS && isSelected(v);
    const getTextColor = (v: MapMarkerType) => {
      if (isSelectedSuggestion(v)) {
        return PRIMARY_6;
      }
      return (v.properties.type !== 'delivering_stop' || v.properties.status === VisitStatus.DEPARTED ? BASE_COLOR : TEXT_SECONDARY_COLOR);
    };
    const getScaledColor = (v: MapMarkerType, altColor: IColor) => (isActiveMarkerColor(v) ? altColor.rgb : altColor.rgbLight);

    const getLayerColor = (v: MapMarkerType, layerType: LayerType) => {
      const completionStatus = v.properties.status;
      let color;
      let completionColor;

      if (completionStatus === VisitStatus.DEPARTED) {
        completionColor = v.properties.pendingColor ? v.properties.pendingColor.rgb : v.properties.color.rgb;
      }

      if (completionStatus === VisitStatus.ARRIVED) {
        completionColor = v.properties.pendingColor ? v.properties.pendingColor.rgb : v.properties.color.rgbLight;
      }

      if (layerType === LayerType.BASE) {
        color = completionColor || BASE_COLOR;
      } else if (layerType === LayerType.TEXT) {
        color = getTextColor(v);
      } else {
        const altColor = v.properties.pendingColor ?? v.properties.color;
        const scaledColor = getScaledColor(v, altColor);
        color = completionColor ? BASE_COLOR : scaledColor;
        if (isSelectedSuggestion(v)) {
          color = PRIMARY_6;
        }
      }

      return color;
    };

    const getIcon = (v: MapMarkerType) => {
      let uiStatus = ICON_LAYER.OUTLINE;

      if (isActive(v)) {
        if (isSelected(v)) {
          uiStatus = ICON_LAYER.SELECTED;
        } else if (isHovered(v)) {
          uiStatus = ICON_LAYER.HOVERED;
        }
      } else {
        uiStatus = ICON_LAYER.BASE;
      }

      return `${getMarkerLength(v)}${uiStatus}`;
    };

    // A low zIndex value prevents marker offsetting too far.
    // When zoomed out, a higher zIndex minimizes zIndex fighting and offsetting is less of a concern.
    const getZoomedZIndex = (v: MapMarkerType, multiplier: number) => {
      const isStartEnd = v.properties.type !== 'delivering_stop';
      const isStartMarker = v.properties.type === 'leaving_start_location';

      let zIndex: number;
      const topMostZIndex = v.properties.rankMax * multiplier;

      if (isActive(v)) {
        // Surface hovered/selected marker to top
        zIndex = topMostZIndex + 1;
      } else if (isStartEnd && v.properties.routeId === hoveredRoute?.id) {
        // Surface start/end marker of hovered route above selected
        zIndex = isStartMarker ? topMostZIndex - 1 : topMostZIndex - 2;
      } else if (isStartEnd && v.properties.routeId === selectedRoute?.id) {
        // Surface start/end marker of selected route above numbered markers
        zIndex = isStartMarker ? topMostZIndex - 3 : topMostZIndex - 4;
      } else {
        zIndex = reverseLinearScale(v.properties.rankMax, 0, v.properties.rank, topMostZIndex);
      }

      return zIndex;
    };

    const getZIndex = (v: MapMarkerType) => {
      let multiplier: number;

      if (zoom && zoom > 15) {
        multiplier = 1;
      } else if (zoom && zoom > 10) {
        multiplier = 2;
      } else if (zoom && zoom > 9) {
        multiplier = 3;
      } else {
        multiplier = 4;
      }

      return getZoomedZIndex(v, multiplier);
    };

    const getPosition = (v: MapMarkerType) => {
      const [longitude, latitude] = v.geometry.coordinates;
      return [longitude, latitude, getZIndex(v)];
    };

    const layerProps = {
      data,
      pickable: false,
      visible,
      getPosition,
      onClick,
      onHover,
      onDragStart,
      onDrag,
      onDragEnd,
      getPolygonOffset: null,
      updateTriggers: {
        getIcon: [hoveredMarker, selectedMarker, selectedMarkers, selectedRoute, hoveredRoute, hoveredMarkers],
        getSize: [hoveredMarker, selectedMarker, selectedMarkers, selectedRoute, hoveredRoute, hoveredMarkers],
        getPosition: [hoveredMarker, selectedMarker, selectedMarkers, selectedRoute, hoveredRoute, hoveredMarkers],
        getColor: [lassoMode, hoveredMarker, selectedMarker, selectedMarkers, selectedRoute, hoveredRoute, hoveredMarkers],
        getPixelOffset: [hoveredMarker, selectedMarker, selectedMarkers, selectedRoute, hoveredRoute, hoveredMarkers],
      },
    };

    const pinProps = {
      ...layerProps,
      getSize: (v: MapMarkerType) => getMarkerSize(v, isActive(v)),
      iconAtlas: '/icon-map.png',
      iconMapping: ICON_MAPPING,
      transitions: {
        getIcon: MARKER_ICON_TRANSITION_DURATION,
        getSize: {
          duration: MARKER_ICON_TRANSITION_DURATION,
          enter: () => 0,
        },
      },
    };

    const MarkerShadowLayerProps = this.getSubLayerProps({
      ...pinProps,
      id: 'icon-shadow',
      getIcon: (v: MapMarkerType) => `${getMarkerLength(v)}${ICON_LAYER.SHADOW}`,
      getSize: (v: MapMarkerType) => getMarkerSize(v, isActive(v)),
      getColor: () => [0, 0, 0, 1], // fade in end
      transitions: {
        getSize: MARKER_ICON_TRANSITION_DURATION,
        getColor: {
          duration: MARKER_ICON_TRANSITION_DURATION,
          enter: () => [0, 0, 0, 0], // fade in start
        },
      },
    });

    const MarkerBackgroundLayerProps = this.getSubLayerProps({
      ...pinProps,
      id: 'icon-outline',
      pickable: true,
      getIcon: (v: MapMarkerType) => `${getMarkerLength(v)}${ICON_LAYER.OUTLINE}`,
      getColor: (v: MapMarkerType) => getLayerColor(v, LayerType.OUTLINE),
    });

    const MarkerForegroundLayerProps = this.getSubLayerProps({
      ...pinProps,
      getPolygonOffset: null,
      id: 'icon-bg',
      getIcon: (v: MapMarkerType) => getIcon(v),
      getColor: (v: MapMarkerType) => getLayerColor(v, LayerType.BASE),
    });

    // @ts-ignore types are not published yet for custom composites
    const stopTextLayerProps = this.getSubLayerProps({
      ...layerProps,
      id: 'text',
      characterSet: [' ', '\u2013', '\u002C', '0', '1', '2', '3', '4', '5', '6', '7', '8', '9', ''],
      getText: (v: MapMarkerType) => getMarkerText(v),
      getSize: (v: MapMarkerType) => getContentSize(v, LayerType.TEXT, isActive(v)),
      getColor: (v: MapMarkerType) => getLayerColor(v, LayerType.TEXT),
      getPixelOffset: (v: MapMarkerType) => getPixelOffset(v, isActive(v)),
      fontFamily: '"proxima-nova", "Helvetica Neue", sans-serif',
      getTextAnchor: 'middle',
      getAlignmentBaseline: 'center',
      transitions: {
        ...BASE_TRANSITION,
        getSize: MARKER_ICON_TRANSITION_DURATION,
        getColor: {
          duration: MARKER_ICON_TRANSITION_DURATION,
          enter: () => [0, 0, 0, 0], // fade in start
        },
      },
    });

    // @ts-ignore types are not published yet for custom composites
    const stopStatusLayerProps = this.getSubLayerProps({
      ...layerProps,
      id: 'status',
      iconAtlas: '/icon-map.png',
      iconMapping: ICON_MAPPING,
      getIcon: (v: MapMarkerType) => ((v.properties.type !== 'delivering_stop') ? v.properties.type : undefined),
      getSize: (v: MapMarkerType) => getContentSize(v, LayerType.STATUS, isActive(v)),
      getColor: (v: MapMarkerType) => getLayerColor(v, LayerType.STATUS),
      getPixelOffset: (v: MapMarkerType) => getPixelOffset(v, isActive(v)),
      transitions: {
        ...BASE_TRANSITION,
        getSize: MARKER_ICON_TRANSITION_DURATION,
      },
    });

    return [
      new IconLayer(MarkerShadowLayerProps),
      new IconLayer(MarkerBackgroundLayerProps),
      new IconLayer(MarkerForegroundLayerProps),
      new TextLayer(stopTextLayerProps),
      new IconLayer(stopStatusLayerProps),
    ];
  }
}

MarkerIconLayer.layerName = 'MarkerIconLayer';

export default MarkerIconLayer;
